import { useMemo, useState } from 'react'
import clsx from 'clsx'
import { Building2 } from 'lucide-react'

import type { CrmContact } from '@/services/crm'
import type {
  RelationshipDefinitions,
  RelationshipRow,
  RelationshipStage,
} from '@/services/crmRelationships'
import { NOT_ON_RECORD, contactInitials, relativeTime, unsureTitle } from '../crmFormat'
import HealthRing from './HealthRing'
import Sparkline from './Sparkline'
import { STAGE_ORDER, STAGE_STYLE, compareCards, dealsLine, volumeCoverage } from './relationshipFormat'

export interface BoardEntry {
  contact: CrmContact
  row: RelationshipRow
}

/** Cards drawn per column before "Show more": a directory of ten thousand
 *  must not mount ten thousand buttons. */
export const CARDS_PER_COLUMN = 30

const FALLBACK_HINT: Record<RelationshipStage, string> = {
  core: 'Frequent and recent',
  active: 'In touch lately, or a deal open',
  cooling: 'On deals, then went quiet',
  new: 'First seen in the last 30 days',
}

/**
 * The relationship board: one column per stage, one card per person.
 *
 * Every card is a real button (focusable, with a visible ring, activated by
 * Enter or Space). What a card shows is what the row carries: the health ring
 * only when there is a score, the sparkline only when there is dated activity,
 * and a sentence saying why the person is in this column.
 */
export default function RelationshipBoard({
  entries,
  definitions,
  selectedId,
  onSelect,
  onOpen,
  loading = false,
  emptyMessage = 'Nobody here with these filters',
}: {
  entries: BoardEntry[]
  definitions?: RelationshipDefinitions
  selectedId: string | null
  onSelect: (id: string) => void
  onOpen: (id: string) => void
  loading?: boolean
  emptyMessage?: string
}) {
  const [expanded, setExpanded] = useState<Partial<Record<RelationshipStage, boolean>>>({})

  const columns = useMemo(
    () =>
      STAGE_ORDER.map((stage) => ({
        stage,
        entries: entries.filter((e) => e.row.stage === stage).sort((a, b) => compareCards(a, b, stage)),
      })),
    [entries],
  )
  const hints = useMemo(
    () => new Map((definitions?.stages ?? []).map((s) => [s.key, s.hint])),
    [definitions],
  )
  const boardIsEmpty = !loading && columns.every((c) => c.entries.length === 0)

  return (
    <section aria-label="Relationship board" className="flex flex-col gap-3">
      {boardIsEmpty ? (
        <div
          role="status"
          className="rounded-lg border border-dashed border-amber-400/15 px-4 py-6 text-center text-[12px] italic text-stone-500"
        >
          {emptyMessage}
        </div>
      ) : null}
      <div className="flex flex-wrap items-start gap-3">
        {columns.map(({ stage, entries: stageEntries }) => {
          const style = STAGE_STYLE[stage]
          const showAll = expanded[stage] || stageEntries.length <= CARDS_PER_COLUMN
          const shown = showAll ? stageEntries : stageEntries.slice(0, CARDS_PER_COLUMN)
          return (
            <div
              key={stage}
              role="group"
              aria-label={`${style.title}, ${stageEntries.length}`}
              data-stage={stage}
              className="flex min-w-0 flex-1 basis-[210px] flex-col gap-2"
            >
              <div className="flex items-center justify-between px-0.5">
                <div className="flex items-center gap-2">
                  <span className={clsx('h-2 w-2 rounded-full', style.dot)} aria-hidden="true" />
                  <span className="text-[12px] font-semibold text-stone-100">{style.title}</span>
                  <span className="font-mono text-[11px] text-stone-500">{loading ? '…' : stageEntries.length}</span>
                </div>
              </div>
              <div className="-mt-1 px-0.5 text-[10.5px] text-stone-600">{hints.get(stage) ?? FALLBACK_HINT[stage]}</div>
              {loading ? (
                <div className="space-y-2" aria-hidden="true">
                  {[0, 1].map((i) => (
                    <div key={i} className="h-[104px] animate-pulse rounded-lg border border-amber-400/[0.07] bg-stone-800/30 motion-reduce:animate-none" />
                  ))}
                </div>
              ) : stageEntries.length === 0 ? (
                <div className="rounded-lg border border-dashed border-stone-700/60 px-3 py-4 text-center text-[11px] text-stone-600">
                  Nobody at this stage
                </div>
              ) : (
                <>
                  {shown.map((entry) => (
                    <RelationshipCard
                      key={entry.contact.id}
                      entry={entry}
                      selected={entry.contact.id === selectedId}
                      onSelect={onSelect}
                      onOpen={onOpen}
                    />
                  ))}
                  {!showAll ? (
                    <button
                      type="button"
                      onClick={() => setExpanded((e) => ({ ...e, [stage]: true }))}
                      className="rounded-md border border-amber-400/10 px-2 py-1.5 text-[11px] text-stone-400 hover:border-amber-400/30 hover:text-amber-200 focus-visible:outline focus-visible:outline-1 focus-visible:outline-amber-400/60"
                    >
                      Show {stageEntries.length - CARDS_PER_COLUMN} more
                    </button>
                  ) : null}
                </>
              )}
            </div>
          )
        })}
      </div>
    </section>
  )
}

function RelationshipCard({
  entry,
  selected,
  onSelect,
  onOpen,
}: {
  entry: BoardEntry
  selected: boolean
  onSelect: (id: string) => void
  onOpen: (id: string) => void
}) {
  const { contact, row } = entry
  const entity = contact.kind === 'organisation' || contact.isEntity
  const cooling = row.stage === 'cooling'
  return (
    <button
      type="button"
      aria-pressed={selected}
      data-contact-id={contact.id}
      onClick={() => onSelect(contact.id)}
      onDoubleClick={() => onOpen(contact.id)}
      className={clsx(
        'flex w-full flex-col gap-2.5 rounded-lg border p-3 text-left transition-colors focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-amber-400/70',
        selected
          ? 'border-amber-400/45 bg-amber-400/[0.07]'
          : 'border-white/[0.08] bg-black/30 hover:border-amber-400/25',
      )}
    >
      <span className="flex w-full min-w-0 items-center gap-2.5">
        <span
          aria-hidden="true"
          className={clsx(
            'flex h-8 w-8 flex-none items-center justify-center border border-amber-400/20 bg-amber-400/[0.07] text-[11px] font-semibold text-amber-200',
            entity ? 'rounded-md' : 'rounded-full',
          )}
        >
          {entity ? <Building2 size={13} /> : contactInitials(contact)}
        </span>
        <span className="flex min-w-0 flex-1 flex-col">
          <span className="flex min-w-0 items-center gap-1.5">
            <span className="truncate text-[12.5px] font-semibold text-stone-100">{contact.displayName}</span>
            {contact.needsReview ? (
              <span
                className="h-1.5 w-1.5 flex-none rounded-full bg-amber-400"
                role="img"
                aria-label={unsureTitle(contact.reviewReason)}
                title={unsureTitle(contact.reviewReason)}
              />
            ) : null}
          </span>
          <span className="truncate text-[11px] text-stone-500">{contact.company ?? NOT_ON_RECORD}</span>
        </span>
        <HealthRing row={row} className="flex-none" />
      </span>
      <span className="flex w-full items-end justify-between gap-2">
        <span className="flex min-w-0 flex-col">
          <span className="truncate text-[12px] text-stone-200" title={row.volume !== null ? volumeCoverage(row) : undefined}>
            {dealsLine(row, contact)}
          </span>
          <span className="truncate text-[11px] text-stone-500" title={row.lastTouch ?? undefined}>
            {row.lastTouch ? `Last touch ${relativeTime(row.lastTouch)}` : 'No dated touch on record'}
          </span>
        </span>
        <Sparkline values={row.spark} tone={cooling ? 'cooling' : 'neutral'} className="flex-none" />
      </span>
      {row.reason ? (
        <span className={clsx('block text-[11px] leading-snug', cooling ? 'text-orange-300/90' : 'text-stone-400')}>
          {row.reason}
        </span>
      ) : null}
    </button>
  )
}
